import {DataInterval} from '@app/layout/sitemanager/meters/meters.service';
import moment, {Duration} from 'moment-timezone';
import {DurationTuple} from '@shared/api/model/timestamp';

export enum Period {
  MINUTES = 'Minutes',
  HOURS = 'Hours',
  DAYS = 'Days',
  WEEKS = 'Weeks',
  MONTHS = 'Months',
  REFERENCE_LINES = 'Reference Lines'
}

export class TimePeriod {
  private static readonly SEPARATOR = '_';
  private static readonly CODE_LENGTH = 2;

  private readonly period: Period;
  private readonly amount: number;

  static fromHashCode(hashCode: string): TimePeriod | null {
    if (hashCode) {
      const [period, amountStr] = hashCode.split(TimePeriod.SEPARATOR);
      const periodCorrectCase = period.charAt(0).toUpperCase() + period.toLowerCase().substring(1) ;
      const amount: number = parseInt(amountStr, 10);
      if (!isNaN(amount)) {
        for (const periodEnum in Period) {
          if (Period.hasOwnProperty(periodEnum)) {
            const periodName: string = Period[periodEnum];
            if (periodName.startsWith(periodCorrectCase)) {
              return new TimePeriod(<Period>periodName, amount);
            }
          }
        }
      }
    }
    return null;
  }

  static fromDataInterval(interval: DataInterval): TimePeriod | null {
    if (interval) {
      switch (interval) {
        case DataInterval.PT5M:
          return new TimePeriod(Period.MINUTES, 5);
        case DataInterval.PT15M:
          return new TimePeriod(Period.MINUTES, 15);
        case DataInterval.PT30M:
          return new TimePeriod(Period.MINUTES, 30);
        case DataInterval.PT1H:
          return new TimePeriod(Period.HOURS, 1);
        case DataInterval.P1D:
          return new TimePeriod(Period.DAYS, 1);
        case DataInterval.P1W:
          return new TimePeriod(Period.WEEKS, 1);
        case DataInterval.P1M:
          return new TimePeriod(Period.MONTHS, 1);
        default:
          throw new Error('Unable to convert DataInterval ' + interval + ' to TimePeriod');
      }
    }
    return null;
  }

  constructor(period: Period, amount?: number) {
    this.period = period;
    if (!amount) {
      this.amount = 1;
    } else {
      this.amount = amount;
    }
  }

  get text(): string {
    if (this.period === Period.REFERENCE_LINES) {
      return this.period;
    } else {
      return this.amount + ' ' + (this.amount > 1 ? this.period : this.period.slice(0, -1));
    }
  }

  getHashCode(): string {
    return this.amount ? (this.period.substring(0, TimePeriod.CODE_LENGTH).toUpperCase() + TimePeriod.SEPARATOR + this.amount) : null;
  }

  isEqual(timePeriod: TimePeriod): boolean {
    return timePeriod && this.period === timePeriod.period && this.amount === timePeriod.amount;
  }

  toMomentDuration(): Duration {
    const momentTuple = this.toDurationTuple();
    return momentTuple ? moment.duration(momentTuple.amount, momentTuple.unit) : null;
  }

  toDurationTuple(): DurationTuple {
    if (this.period === Period.REFERENCE_LINES) {
      return null;
    } else {
      const durationStr: string = <string>this.period;
      const durationUnit: moment.unitOfTime.Base = <moment.unitOfTime.Base>durationStr.toLowerCase();
      return {amount: this.amount, unit: durationUnit};
    }
  }

  getPeriod(): Period {
    return this.period;
  }

  getAmount(): number {
    return this.amount;
  }
}
